import { type Location, type To } from "react-router-dom";

import { Route } from "~services/router/Route";

const STORAGE_KEY = "postLoginRedirect";

const AUTH_FLOW_PATHS = new Set([
  Route.index(),
  Route.login(),
  Route.verify(),
  Route.profileCreate(),
  Route.logout(),
  Route.loginCallback(),
]);

export interface StoredPostLoginRedirect {
  hash: string;
  pathname: string;
  search: string;
}

function isPersistableRedirectPath(pathname: string | undefined): pathname is string {
  return Boolean(pathname && !AUTH_FLOW_PATHS.has(pathname));
}

function toStoredRedirect(location: Pick<Location, "hash" | "pathname" | "search">): StoredPostLoginRedirect {
  return {
    hash: location.hash ?? "",
    pathname: location.pathname,
    search: location.search ?? "",
  };
}

function storedToNavigationTarget(stored: StoredPostLoginRedirect): To {
  return {
    hash: stored.hash,
    pathname: stored.pathname,
    search: stored.search,
  };
}

export function storePostLoginRedirect(location: Pick<Location, "hash" | "pathname" | "search">): void {
  if (!isPersistableRedirectPath(location.pathname)) {
    return;
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStoredRedirect(location)));
}

export function peekPostLoginRedirect(): StoredPostLoginRedirect | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredPostLoginRedirect;
    if (!isPersistableRedirectPath(parsed.pathname)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function clearPostLoginRedirect(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

function resolveFromRouterState(stateFrom?: Location | null): To | null {
  if (!isPersistableRedirectPath(stateFrom?.pathname)) {
    return null;
  }

  return {
    hash: stateFrom.hash ?? "",
    pathname: stateFrom.pathname,
    search: stateFrom.search ?? "",
  };
}

export function resolvePostLoginRedirect(stateFrom?: Location | null): To {
  const fromState = resolveFromRouterState(stateFrom);
  if (fromState) {
    return fromState;
  }

  const stored = peekPostLoginRedirect();
  if (stored) {
    return storedToNavigationTarget(stored);
  }

  return Route.home();
}

export function consumePostLoginRedirect(stateFrom?: Location | null): To {
  const fromState = resolveFromRouterState(stateFrom);
  if (fromState) {
    clearPostLoginRedirect();
    return fromState;
  }

  const stored = peekPostLoginRedirect();
  clearPostLoginRedirect();
  if (stored) {
    return storedToNavigationTarget(stored);
  }

  return Route.home();
}
